import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { updateNews, getAllCommittee, getListOfExcos } from '../../utils/api-calls';
import styled from "styled-components";
import { rel8Pink, rel8Purple, rel8White } from "../../globals";
import { mobile } from "../../responsive";
import TextEditor from "../TextEditor";
import Loading from "../Loading/Loading";
import { ImageFieldWithPreview } from '../ImageFieldWithPreview';

const BackDrop = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const SubCon = styled.div`
  background-color: ${rel8White};
  width: 350px;
  max-height: 80%;
  border-radius: 10px;
  padding: 20px;
  overflow-y: auto;

  ${mobile({
    width: "250px",
  })}
`;

const SubConHeader = styled.p`
  font-weight: 700;
  text-align: center;
`;

const Form = styled.form`
  margin: 20px 0px;
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  margin: 10px 0px;
`;

const FormDataComp = styled.input`
  padding: 5px 0px;
  background-color: transparent;
  border: none;
  border: 1px solid ${rel8Purple};
  border-radius: 5px;
  padding: 5px;
  color: ${rel8Purple};
  outline: none;
  &::placeholder {
    color: ${rel8Purple};
  }
`;

const FormSelection = styled.select`
  padding: 5px 0px;
  color: ${rel8Purple};
  outline: none;
  border: none;
  border-bottom: 1px solid ${rel8Purple};
  margin: 10px 0px;
  overflow: auto;
`;

const FormOption = styled.option``;

const SubConBtnHold = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-top: 20px;
`;

const SubConBtn = styled.input`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: ${(props) =>
    props.typex === "filled" ? `${rel8Purple}` : `${rel8Pink}`};
  color: ${(props) =>
    props.typex === "filled" ? `${rel8White}` : `${rel8Purple}`};
  cursor: pointer;
`;


const EditNewsModal = ({ show, onClose, data, onSubmit }) => {
  const [content, setContent] = useState(data?.body || '');
  const [formData, setFormData] = useState({
    name: data?.name || '',
    is_for_members_only: data?.is_for_members_only || false,
    is_exco: data?.is_exco || false,
    exco_id: data?.exco_id,
    is_committe: data?.is_committe || false,
    commitee_name: data?.commitee_name || '',
    is_member: data?.is_member || false
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        is_for_members_only: data.is_for_members_only,
        is_exco: data.is_exco,
        exco_id: data.exco_id,
        is_committe: data.is_committe,
        commitee_name: data.commitee_name || '',
        is_member: data.is_member
      });
      setContent(data?.body || '');
    }
  }, [data]);

  const {
    isLoading: excoListLoading,
    isFetching: excoListFetching,
    isError: excoListIsError,
    // data: excoListData,
  } = useQuery ("exco-list", getListOfExcos, {
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.data
        .map((item) => ({ id: item.id, name: item.name }))
        .reverse();
    },
  });

  const {
    isLoading: committeeLoading,
    isError: committeeError,
    isFetching: committeeFetching,
    data: committeeData,
  } = useQuery("all-committees", getAllCommittee, {
    refetchOnWindowFocus: false,
    select: (data) => {
      const result = data.data.map((item) => ({
        id: item.id,
        name: item.name,
      }));
      return result.sort((a, b) => a.id - b.id);
    },
  });

  const { isLoading, mutate: updateNewsMutation } = useMutation(
    (newsData) => updateNews(newsData.id, newsData.data),
    {
      onMutate: () => {
        toast.info("News update in progress", {
          progressClassName: "toastProgress",
          icon: false,
        });
      },
      onSuccess: (response) => {
        toast.success("News updated successfully", {
          progressClassName: "toastProgress",
          icon: false,
        });
        onSubmit(response);
        onClose();
      },
      onError: (error) => {
        toast.error(`Can't update news: ${error.message}`);
      },
    }
  );

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (e.target.files) {
      name = 'image';
      value = e.target.files[0];
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      body: content,
      ...formData
    }

    updateNewsMutation({ id: data.id, data: payload });
  };

  if (!show) return null;

  return (
    <BackDrop>
      <style>
        {`
          body {
            overflow: hidden;
          }
        `}
      </style>
      {excoListLoading ||
      excoListFetching ||
      committeeLoading ||
      committeeFetching ? (
        <Loading
          loading={
            excoListLoading ||
            excoListFetching ||
            committeeLoading ||
            committeeFetching
          }
        />
      ) : !excoListIsError || !committeeError ? (
        <SubCon style={{ width: "600px" }}>
          <SubConHeader>Edit News</SubConHeader>
          <Form onSubmit={handleSubmit}>
            <FormLabel>
              Name:
              <FormDataComp
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </FormLabel>

            <FormLabel>
              For Members Only:
              <FormSelection
                name="is_for_members_only"
                value={formData.is_for_members_only}
                onChange={handleChange}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>

            {/* <FormLabel>
              Is Exco:
              <FormSelection
                name="is_exco"
                value={formData.is_exco}
                onChange={handleChange}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>

            {formData.is_exco === true && (
              <FormLabel>
                Excos Id:
                <FormSelection
                  name="exco_id"
                  value={formData.exco_id}
                  onChange={handleChange}
                >
                  <FormOption disabled value="">
                    select an option
                  </FormOption>
                  {excoListData.map((item) => (
                    <FormOption key={item.id} value={item.id}>
                      {item.id} || {item.name}
                    </FormOption>
                  ))}
                </FormSelection>
              </FormLabel>
            )} */}

            <FormLabel>
              Is Committe:
              <FormSelection
                name="is_committe"
                value={formData.is_committe}
                onChange={handleChange}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>

            {formData.is_committe === true && (
              <FormLabel>
                Committe Name:
                <FormSelection
                  name="commitee_name"
                  value={formData.commitee_name}
                  onChange={handleChange}
                >
                  {committeeData.map((item) => (
                    <FormOption key={item.id} value={item.id}>
                      {item.id} || {item.name}
                    </FormOption>
                  ))}
                </FormSelection>
              </FormLabel>
            )}

            <FormLabel>
              Is Member:
              <FormSelection
                name="is_member"
                value={formData.is_member}
                onChange={handleChange}
              >
                <FormOption disabled value="">
                  select an option
                </FormOption>
                <FormOption value={true}>Yes</FormOption>
                <FormOption value={false}>No</FormOption>
              </FormSelection>
            </FormLabel>

            <ImageFieldWithPreview
              imageUrl={data.image}
              onChange={handleChange}
            />

            <TextEditor
              name={"body"}
              value={content}
              onChange={setContent}
              placeholder="Write your news content here..."
            />

            <SubConBtnHold>
              <SubConBtn type="submit" value="Save" typex="filled" disabled={isLoading} />
              <SubConBtn type="button" value="Cancel" onClick={onClose} />
            </SubConBtnHold>
          </Form>
        </SubCon>
      ) : (
        <small style={{ color: "white", fontSize: "20px" }}>
          An error occured while fetching data. Kindly refresh page.
        </small>
      )}
    </BackDrop>
  );
};

export default EditNewsModal;